import { cloneElement, useEffect, useId, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  Sparkles,
  Hash,
  CheckSquare,
  FolderOpen,
  Phone,
  Search,
  Settings,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  ChevronDown,
  MessageSquare,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/cn";

import { useRooms } from "@/lib/rooms";
import type { Room } from "@/lib/types";
import { openCommandPalette } from "@/lib/uiState";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { useWorkspaces, useWorkspace, useWorkspaceMemberships, switchWorkspace, WorkspaceMfaRequired, accountTypeLabel } from "@/lib/workspace";
import { useLogout, useSession } from "@/lib/auth";
import { WorkspaceManagementDialog } from "@/components/WorkspaceManagementDialog";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

interface SidebarProps {
  /** Slim 64 px presentation. Drawer mode passes `false`. */
  collapsed?: boolean;
  /** Click handler for "expand/collapse" toggle (desktop only). */
  onToggleCollapsed?: () => void;
  /** Called after a navigation triggers — used by the mobile drawer to close itself. */
  onNavigate?: () => void;
  className?: string;
}

interface NavItem {
  to: string;
  icon: typeof Home;
  labelKey: string;
  /** Match prefix instead of exact path. */
  end?: boolean;
  /** Live unread count badge. */
  badge?: number;
}

/* -------------------------------------------------------------------------- */
/* Sidebar                                                                    */
/* -------------------------------------------------------------------------- */

export function Sidebar({
  collapsed = false,
  onToggleCollapsed,
  onNavigate,
  className,
}: SidebarProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useLogout();
  const workspace = useWorkspace();

  const { rooms } = useRooms();
  const pinnedRooms = rooms.filter((r) => r.pinned);
  const channels = rooms.filter((r) => !r.pinned && r.kind === "channel");
  const dms = rooms.filter((r) => r.kind === "dm");

  const totalUnread = useMemo(
    () => rooms.reduce((sum, r) => sum + (r.unreadCount ?? 0), 0),
    [rooms],
  );
  const dmUnread = useMemo(
    () => dms.reduce((sum, r) => sum + (r.unreadCount ?? 0), 0),
    [dms],
  );

  const NAV_ITEMS: NavItem[] = [
    { to: "/", icon: Home, labelKey: "nav.home", end: true },
    { to: "/rooms", icon: Hash, labelKey: "nav.rooms", badge: totalUnread - dmUnread },
    { to: "/dm", icon: MessageSquare, labelKey: "nav.dm", badge: dmUnread },
    { to: "/agents", icon: Sparkles, labelKey: "nav.agents" },
    { to: "/tasks", icon: CheckSquare, labelKey: "nav.tasks" },
    { to: "/files", icon: FolderOpen, labelKey: "nav.files" },
    { to: "/calls", icon: Phone, labelKey: "call.start" },
  ];

  return (
    <nav
      role="navigation"
      aria-label={t("nav.workspace")}
      className={cn(
        "h-full bg-surface-base border-r border-border-subtle",
        "flex flex-col text-base select-none",
        className,
      )}
    >
      {/* ---------- Workspace switcher ---------- */}
      <WorkspaceSwitcher
        collapsed={collapsed}
        onToggleCollapsed={onToggleCollapsed}
      />

      {/* ---------- Search trigger ---------- */}
      <div className={cn("px-3 pt-3", collapsed && "px-2")}>
        {collapsed ? (
          <IconButton
            label={t("search.placeholder")}
            onClick={openCommandPalette}
            icon={<Search size={16} aria-hidden />}
          />
        ) : (
          <button
            type="button"
            onClick={openCommandPalette}
            className={cn(
              "w-full h-9 px-3 flex items-center gap-2 rounded-md",
              "bg-canvas border border-border-default text-fg-muted text-sm",
              "hover:bg-surface-raised hover:border-border-strong/40",
              "transition-colors duration-150 ease-out",
              "focus-visible:shadow-focus",
            )}
          >
            <Search size={14} aria-hidden />
            <span className="flex-1 text-left">{t("search.placeholder")}</span>
            <kbd className="text-[10px] text-fg-secondary px-1.5 py-0.5 rounded bg-surface-raised border border-border-subtle font-mono">
              Ctrl K
            </kbd>
          </button>
        )}
      </div>

      {/* ---------- Primary navigation ---------- */}
      <ul className={cn("mt-3", collapsed ? "px-2" : "px-2")}>
        {NAV_ITEMS.map(({ to, icon: Icon, labelKey, end, badge }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={end}
              onClick={onNavigate}
              title={collapsed ? t(labelKey) : undefined}
              className={({ isActive }) =>
                cn(
                  "group relative flex items-center gap-2.5 rounded-md text-sm",
                  "transition-colors duration-150 ease-out",
                  "focus-visible:shadow-focus",
                  collapsed
                    ? "h-9 w-full justify-center"
                    : "px-3 py-1.5",
                  isActive
                    ? "bg-brand-100 text-brand-700 font-medium"
                    : "text-fg-secondary hover:bg-surface-raised hover:text-fg-primary",
                )
              }
            >
              <Icon size={16} aria-hidden className="shrink-0" />
              {!collapsed && (
                <>
                  <span className="flex-1 truncate">{t(labelKey)}</span>
                  {badge && badge > 0 ? (
                    <Badge tone="brand" className="text-[10px] px-1.5 py-0">
                      {badge > 99 ? "99+" : badge}
                    </Badge>
                  ) : null}
                </>
              )}
              {collapsed && badge && badge > 0 ? (
                <span
                  aria-hidden
                  className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-brand-600 text-fg-inverse text-[10px] font-medium flex items-center justify-center"
                >
                  {badge > 9 ? "9+" : badge}
                </span>
              ) : null}
            </NavLink>
          </li>
        ))}
      </ul>

      {/* ---------- Channel groups ---------- */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden mt-3 pb-3">
        {!collapsed && (
          <>
            <SidebarGroup
              title={t("nav.pinned")}
              icon={<Pin size={12} aria-hidden />}
              rooms={pinnedRooms}
              onNavigate={onNavigate}
            />
            <SidebarGroup
              title={t("nav.rooms")}
              icon={<Hash size={12} aria-hidden />}
              rooms={channels}
              onNavigate={onNavigate}
              addLabel={t("nav.rooms")}
            />
            <SidebarGroup
              title={t("nav.dm")}
              icon={null}
              rooms={dms}
              onNavigate={onNavigate}
              showAvatar
            />
          </>
        )}
      </div>

      {!collapsed && workspace.data?.branding?.show_company_footer && <p className="px-4 py-2 text-xs text-fg-muted truncate">{workspace.data.name}</p>}
      {/* ---------- User menu ---------- */}
      <UserMenu
        collapsed={collapsed}
        onSettings={() => {
          navigate("/settings");
          onNavigate?.();
        }}
        onLogout={() => {
          logout();
          navigate("/login");
        }}
      />
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/* Workspace switcher                                                         */
/* -------------------------------------------------------------------------- */

interface Workspace {
  id: string;
  name: string;
  plan: "free" | "starter" | "business" | "enterprise";
}


function WorkspaceSwitcher({
  collapsed,
  onToggleCollapsed,
}: {
  collapsed: boolean;
  onToggleCollapsed?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const workspaces = useWorkspaces();
  const memberships=useWorkspaceMemberships();
  const session=useSession();
  const navigate = useNavigate();
  const available=memberships.data?.workspaces ?? workspaces.data ?? [];
  const activeMembership = available.find(workspace=>workspace.active||workspace.id===session?.tenantId) ?? workspaces.data?.[0];
  const active = activeMembership ? {...workspaces.data?.find(workspace=>workspace.id===activeMembership.id),...activeMembership} : undefined;
  const [mode,setMode]=useState<"create"|"manage"|null>(null),[busy,setBusy]=useState(false),[error,setError]=useState<string|null>(null);
  const select=async(id:string)=>{if(id===active?.id)return;setBusy(true);setError(null);try{await switchWorkspace(id);navigate("/rooms",{replace:true});}catch(e){
    if(e instanceof WorkspaceMfaRequired)navigate("/login?reauth=1",{state:{mfaChallenge:e.challenge,from:{pathname:"/rooms"}}});else setError(e instanceof Error?e.message:String(e));
  }finally{setBusy(false);}};

  return (
    <><div
      className={cn(
        "h-14 border-b border-border-subtle flex items-center gap-2",
        collapsed ? "justify-center px-2" : "px-3",
      )}
    >
      {
        <DropdownMenu
          trigger={
            <button
              type="button"
              aria-label={collapsed?`${active?.name || "ワークスペース"}を切り替える`:undefined}
              disabled={busy}
              className={cn(
                "flex-1 min-w-0 h-9 px-2 flex items-center gap-2 rounded-md",
                "hover:bg-surface-raised text-left",
                "transition-colors duration-150 ease-out",
                "focus-visible:shadow-focus",
              )}
            >
              {active?.branding?.show_logo !== false && <BridgeMark className="text-brand-600 shrink-0" />}
              {!collapsed&&<span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-fg-primary truncate">
                  {active?.name || "ワークスペース"}
                </span>
                <span className="block text-[11px] text-fg-muted truncate">
                  {accountTypeLabel(active?.account_type)}
                </span>
              </span>}
              {!collapsed&&<ChevronDown size={14} aria-hidden className="text-fg-muted shrink-0" />}
            </button>
          }
          ariaLabel={t("nav.workspace")}
        >
          {(close) => (
            <>
              <p className="px-3 py-1.5 text-[11px] font-semibold text-fg-muted uppercase tracking-wide">
                ワークスペースを切り替える
              </p>
              {available.map((ws) => (
                <button
                  key={ws.id}
                  type="button"
                  role="menuitem"
                  disabled={busy || ws.switchable===false}
                  onClick={() => { close(); void select(ws.id); }}
                  className={cn(
                    "w-full px-3 py-2 flex items-center gap-2.5 text-sm text-left",
                    "hover:bg-surface-raised focus-visible:bg-surface-raised focus-visible:outline-none",
                    ws.id === active?.id && "bg-brand-50 text-brand-700",
                  )}
                >
                  <span className="w-7 h-7 rounded-md bg-brand-100 text-brand-700 inline-flex items-center justify-center text-xs font-bold shrink-0">
                    {ws.name.slice(0, 1)}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium truncate">{ws.name}</span>
                    <span className="block text-[11px] text-fg-muted truncate">
                      {accountTypeLabel(ws.account_type)}
                    </span>
                  </span>
                </button>
              ))}
              <div className="my-1 border-t border-border-subtle" />
              <button
                type="button"
                role="menuitem"
                onClick={() => { close(); setMode("manage"); }}
                className="w-full px-3 py-2 flex items-center gap-2 text-sm text-left hover:bg-surface-raised text-fg-secondary"
              >
                <Plus size={14} aria-hidden />
                <span>ワークスペースを管理</span>
              </button>
              {!["guest","viewer"].includes(active?.role || "")&&<button type="button" role="menuitem" onClick={()=>{close();setMode("create");}} className="w-full px-3 py-2 flex items-center gap-2 text-sm text-left hover:bg-surface-raised"><Plus size={14} aria-hidden/><span>ワークスペースを作成</span></button>}
            </>
          )}
        </DropdownMenu>
      }

      {onToggleCollapsed ? (
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={t("shortcuts.toggleSidebar")}
          aria-pressed={collapsed}
          title={t("shortcuts.toggleSidebar")}
          className={cn(
            "p-1.5 rounded-md text-fg-muted",
            "hover:bg-surface-raised hover:text-fg-primary",
            "transition-colors duration-150 ease-out",
            "focus-visible:shadow-focus",
            collapsed && "absolute right-2 hidden",
          )}
        >
          {collapsed ? (
            <PanelLeftOpen size={16} aria-hidden />
          ) : (
            <PanelLeftClose size={16} aria-hidden />
          )}
        </button>
      ) : null}
    </div>
    {(error||memberships.error)&&<p role="alert" className="px-3 py-2 text-xs text-danger">{error || memberships.error?.message}</p>}
    {mode&&<WorkspaceManagementDialog mode={mode} workspace={active} onClose={()=>setMode(null)}/>}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Sidebar group                                                              */
/* -------------------------------------------------------------------------- */

function SidebarGroup({
  title,
  icon,
  rooms,
  onNavigate,
  showAvatar,
  addLabel,
}: {
  title: string;
  icon: ReactNode;
  rooms: Room[];
  onNavigate?: () => void;
  showAvatar?: boolean;
  addLabel?: string;
}): JSX.Element | null {
  const [open, setOpen] = useState(true);
  const headingId = useId();
  const navigate = useNavigate();
  if (rooms.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="mt-3">
      <div className="px-4 mb-1 flex items-center gap-1.5">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "flex-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide",
            "text-fg-muted hover:text-fg-secondary transition-colors duration-150 ease-out",
            "focus-visible:shadow-focus rounded",
          )}
        >
          <ChevronDown
            size={10}
            aria-hidden
            className={cn("transition-transform duration-150 ease-out", !open && "-rotate-90")}
          />
          {icon}
          <span id={headingId}>{title}</span>
        </button>
        {addLabel ? (
          <button
            type="button"
            aria-label={`${addLabel} +`}
            onClick={() => { navigate(showAvatar ? "/dm" : "/rooms"); onNavigate?.(); }}
            title={`${addLabel} +`}
            className="p-0.5 rounded text-fg-muted hover:bg-surface-raised hover:text-fg-primary transition-colors duration-150 ease-out focus-visible:shadow-focus"
          >
            <Plus size={12} aria-hidden />
          </button>
        ) : null}
      </div>
      {open ? (
        <ul className="px-2">
          {rooms.map((room) => (
            <li key={room.id}>
              <NavLink
                to={`/rooms/${room.id}`}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "group flex items-center gap-2 px-2 py-1.5 rounded-md text-sm",
                    "transition-colors duration-150 ease-out",
                    "focus-visible:shadow-focus",
                    isActive
                      ? "bg-brand-100 text-brand-700 font-medium"
                      : room.unreadCount > 0
                        ? "text-fg-primary font-medium hover:bg-surface-raised"
                        : "text-fg-secondary hover:bg-surface-raised hover:text-fg-primary",
                  )
                }
              >
                {showAvatar ? (
                  <Avatar name={room.name} size={20} />
                ) : (
                  <span aria-hidden className="text-fg-muted shrink-0 text-sm">
                    #
                  </span>
                )}
                <span className="flex-1 truncate">{room.name.replace(/^#/, "")}</span>
                {room.unreadCount > 0 ? (
                  <Badge tone="brand" className="text-[10px] px-1.5 py-0 min-w-[20px] justify-center">
                    {room.unreadCount > 99 ? "99+" : room.unreadCount}
                  </Badge>
                ) : null}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* User menu (footer)                                                         */
/* -------------------------------------------------------------------------- */

function UserMenu({
  collapsed,
  onSettings,
  onLogout,
}: {
  collapsed: boolean;
  onSettings: () => void;
  onLogout: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  return (
    <div
      className={cn(
        "border-t border-border-subtle",
        collapsed ? "p-2 flex justify-center" : "p-3",
      )}
    >
      {collapsed ? (
        <DropdownMenu
          trigger={
            <button
              type="button"
              className="rounded-full focus-visible:shadow-focus"
              aria-label={(session?.user.displayName || "アカウント")}
              title={(session?.user.displayName || "アカウント")}
            >
              <Avatar name={(session?.user.displayName || "アカウント")} size={28} imageUrl={session?.user.avatarUrl} />
            </button>
          }
          ariaLabel={t("nav.profile")}
          align="left"
          side="top"
        >
          {(close) => (
            <UserMenuContent
              close={close}
              onSettings={onSettings}
              onLogout={onLogout}
            />
          )}
        </DropdownMenu>
      ) : (
        <DropdownMenu
          trigger={
            <button
              type="button"
              className={cn(
                "w-full flex items-center gap-2 p-1.5 rounded-md text-left",
                "hover:bg-surface-raised transition-colors duration-150 ease-out",
                "focus-visible:shadow-focus",
              )}
              aria-label={t("nav.profile")}
            >
              <Avatar name={(session?.user.displayName || "アカウント")} size={28} imageUrl={session?.user.avatarUrl} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">
                  {(session?.user.displayName || "アカウント")}
                </span>
                <span className="block text-[11px] text-fg-muted truncate">
                  {(session?.user.email || "")}
                </span>
              </span>
              <ChevronDown size={14} aria-hidden className="text-fg-muted shrink-0" />
            </button>
          }
          ariaLabel={t("nav.profile")}
          side="top"
        >
          {(close) => (
            <UserMenuContent
              close={close}
              onSettings={onSettings}
              onLogout={onLogout}
            />
          )}
        </DropdownMenu>
      )}
    </div>
  );
}

function UserMenuContent({
  close,
  onSettings,
  onLogout,
}: {
  close: () => void;
  onSettings: () => void;
  onLogout: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  return (
    <>
      <div className="px-3 py-2 border-b border-border-subtle">
        <p className="text-sm font-medium truncate">{(session?.user.displayName || "アカウント")}</p>
        <p className="text-[11px] text-fg-muted truncate">{(session?.user.email || "")}</p>
      </div>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onSettings();
          close();
        }}
        className="w-full px-3 py-2 flex items-center gap-2 text-sm text-left hover:bg-surface-raised text-fg-secondary"
      >
        <Settings size={14} aria-hidden />
        <span>{t("nav.settings")}</span>
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onLogout();
          close();
        }}
        className="w-full px-3 py-2 flex items-center gap-2 text-sm text-left hover:bg-surface-raised text-danger"
      >
        <LogOut size={14} aria-hidden />
        <span>{t("nav.logout")}</span>
      </button>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Generic dropdown menu (lightweight, focus-aware)                           */
/* -------------------------------------------------------------------------- */

interface DropdownMenuProps {
  trigger: ReactElement;
  ariaLabel: string;
  align?: "left" | "right";
  side?: "top" | "bottom";
  children: (close: () => void) => ReactNode;
}

function DropdownMenu({
  trigger,
  ariaLabel,
  align = "left",
  side = "bottom",
  children,
}: DropdownMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const lastItem = useRef(false);
  const menuId = useId();
  const close = () => { setOpen(false); triggerRef.current?.focus(); };

  useEffect(() => {
    if (!open) return;
    const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)');
    (lastItem.current ? items?.[items.length - 1] : items?.[0])?.focus();
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      {cloneElement(trigger, {
        ref: triggerRef,
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": open ? menuId : undefined,
        onClick: () => { lastItem.current = false; setOpen(v => !v); },
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault(); lastItem.current = e.key === "ArrowUp"; setOpen(true);
          }
        },
      })}
      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={ariaLabel}
          onKeyDown={e => {
            const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') || []);
            const index = items.indexOf(document.activeElement as HTMLElement);
            if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
            else if (e.key === "Tab") setOpen(false);
            else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) {
              e.preventDefault();
              const next = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : (index + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
              items[next]?.focus();
            }
          }}
          className={cn(
            "absolute z-30 min-w-[220px] py-1 rounded-md",
            "bg-canvas border border-border-subtle shadow-md",
            align === "left" ? "left-0" : "right-0",
            side === "top" ? "bottom-full mb-1" : "top-full mt-1",
          )}
        >
          {children(close)}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Icon-only button (collapsed sidebar)                                       */
/* -------------------------------------------------------------------------- */

function IconButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        "w-full h-9 flex items-center justify-center rounded-md",
        "text-fg-muted hover:bg-surface-raised hover:text-fg-primary",
        "transition-colors duration-150 ease-out",
        "focus-visible:shadow-focus",
      )}
    >
      {icon}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Bridge mark logo                                                           */
/* -------------------------------------------------------------------------- */

function BridgeMark({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M2.5 11 V14.25 Q2.5 16.5 4.75 16.5 H6 V19 L9.25 16.5 H17.25 Q19.5 16.5 19.5 14.25 V11 Z" />
      <path d="M6 11 V3 M16 11 V3" />
      <path d="M2.5 11 L6 4.75 Q11 9.25 16 4.75 L19.5 11" />
    </svg>
  );
}
